"use client";

import {
  useCheckout,
  useFiatPricingContext,
  usePassportProvider,
} from "@/context";
import { useLoginModal } from "@/context/LoginModalContext";
import { useGetWalletGems } from "@/hooks/api";
import {
  useCoinBalancesZkEVMQuery,
  useCollectionsQuery,
} from "@/hooks/useQuery";
import { isMobile } from "@/utils/browser";
import { type AllIconKeys, Body, Box, Icon, ShimmerBox } from "@biom3/react";
import { usePathname, useRouter } from "next/navigation";
import React, { useMemo, useCallback } from "react";
import { Avatar } from "./Avatar";
import { GemsBadge, SignInBadge } from "./AvatarBadge";

const CARD_HEIGHT = "260px"; // Allow height to adjust naturally

// Memoize DesktopProfileCard
export const DesktopProfileCard = React.memo(
  ({
    isLoggedIn,
    onProfileClick,
  }: { isLoggedIn?: boolean; onProfileClick: () => void }) => {
    const { walletAddress } = usePassportProvider();
    const { walletGems, isLoading: isGemsLoading } = useGetWalletGems(
      walletAddress || null,
      false,
    );
    const { zkEvmData, starkExData, zkEvmIsLoading, starkExIsLoading } =
      useCollectionsQuery(walletAddress, !!walletAddress);
    const router = useRouter();
    const pathname = usePathname();
    // Calculate inventory count
    const zkEvmCollections =
      zkEvmData?.pages.flatMap((page) => page.collections) || [];
    const starkExCollections =
      starkExData?.pages.flatMap((page) => page.collections) || [];
    const inventoryCount = zkEvmCollections.length + starkExCollections.length;
    const { openLoginModal } = useLoginModal();
    const { checkoutClient, checkoutNetwork, checkoutProvider } = useCheckout();
    const { data: coinBalancesData, isLoading: isBalancesLoading } =
      useCoinBalancesZkEVMQuery(
        walletAddress,
        checkoutClient,
        checkoutNetwork,
        checkoutProvider,
      );
    const { getTotalFiatBalance } = useFiatPricingContext();

    const totalUSDBalance = useMemo(
      () => getTotalFiatBalance(coinBalancesData ?? []),
      [coinBalancesData, getTotalFiatBalance],
    );

    // Shorten wallet address
    const shortAddress = walletAddress
      ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
      : "";

    const handleBalanceClick = useCallback(
      (e: React.MouseEvent) => {
        router.push("/balance");
        e.stopPropagation();
      },
      [router],
    );

    const handleInventoryClick = useCallback(
      (e: React.MouseEvent) => {
        router.push("/inventory");
        e.stopPropagation();
      },
      [router],
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "rgba(26, 26, 26, 0.6)",
          borderRadius: "16px",
          width: "100%",
          p: "6px",
          height: CARD_HEIGHT,
          position: "relative",
          cursor: "pointer",
          margin: "0 auto",
        }}
        onClick={onProfileClick}
      >
        {/* Top Background Section (Sits *below* Avatar/Gems in layout flow, but visually behind due to z-index and overlap) */}
        <Box
          sx={{
            background: "rgba(26, 26, 26, 0.6)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "100%",
            borderTopLeftRadius: "8px", // outer border radius minus padding
            borderTopRightRadius: "8px", // outer border radius minus padding
            height: "80px",
          }}
        />
        {/* Container for Avatar and Gems Badge */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            width: "100%", // Take full width to center content
            mt: "-50px",
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              width: "100%",
            }}
          >
            <Avatar />
            {/* Sub-Avatar badge, adjust negative margin for overlap */}
            {(isLoggedIn === true || isLoggedIn === undefined) && (
              <GemsBadge
                walletGems={walletGems?.gems || ""}
                sx={{ mt: "-14px", zIndex: 3 }}
                isLoading={isGemsLoading || isLoggedIn === undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  router.push("/rewards");
                }}
              />
            )}
            {isLoggedIn === false && (
              <SignInBadge
                sx={{ mt: "-14px", zIndex: 3 }}
                onClick={() =>
                  openLoginModal(async () => {
                    if (isMobile()) router.replace("/quests");
                  })
                }
              />
            )}
          </Box>

          {/* Conditional rendering for logged in/out */}
          {isLoggedIn === true || isLoggedIn === undefined ? (
            <>
              {/* Wallet Address - Placed directly below Avatar/Gems container */}
              <Box
                sx={{
                  position: "relative",
                  cursor: "pointer",
                  display: "inline-block",
                  mt: "base.spacing.x4",
                  width: "fit-content",
                }}
                aria-label="wallet address"
                tabIndex={0}
              >
                {isLoggedIn === undefined ? (
                  <ShimmerBox sx={{ width: "100px", height: "20px" }} />
                ) : (
                  <Body size="small" weight="regular" sx={{ color: "#fff" }}>
                    {shortAddress}
                  </Body>
                )}
              </Box>

              {/* Rows Container - Spaced from Wallet Address */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "base.spacing.x2",
                  mt: "base.spacing.x4",
                }}
              >
                <ProfileRow
                  icon="Wallet"
                  label="Balance"
                  value={`${isBalancesLoading || isLoggedIn === undefined || totalUSDBalance?.amount === undefined ? "" : `$${totalUSDBalance?.amount}`}`}
                  onClick={handleBalanceClick}
                  isSelected={isItemSelected(pathname || "", "/balance")}
                  isLoading={isBalancesLoading || isLoggedIn === undefined}
                />
                <ProfileRow
                  icon="TreasureChest"
                  label="Collections"
                  value={`${inventoryCount}`}
                  onClick={handleInventoryClick}
                  isSelected={isItemSelected(pathname || "", "/inventory")}
                  isLoading={false || isLoggedIn === undefined}
                />
              </Box>
            </>
          ) : (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: "base.spacing.x4",
                px: "base.spacing.x4",
                height: "100px",
              }}
            >
              <Body
                size="medium"
                weight="regular"
                sx={{ color: "#fff", textAlign: "center" }}
              >
                Earn valuable rewards while discovering fantastic new games.
              </Body>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);

// Memoize ProfileRow
const ProfileRow = React.memo(
  ({
    icon,
    label,
    value,
    onClick,
    isSelected,
    isLoading,
  }: {
    icon: AllIconKeys;
    label: string;
    value: string;
    onClick: (e: React.MouseEvent) => void;
    isSelected: boolean;
    isLoading: boolean;
  }) => {
    return (
      <Box
        sx={{
          width: "100%", // Takes width from parent padding
          display: "flex",
          alignItems: "center",
          gap: "10px",
          background: isSelected ? "rgba(243, 243, 243, 0.15)" : "#1A1A1A", // Row background
          borderRadius: "999px", // Pill shape
          px: "base.spacing.x4", // Internal padding for row content
          py: "base.spacing.x2",
          cursor: "pointer",
          transition: "background-color 0.2s ease-in-out",
          "&:hover": {
            background: "rgba(243, 243, 243, 0.15)",
          },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        data-testid={`profile-row-${label.toLowerCase()}`}
      >
        <Icon
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          icon={icon as any}
          variant="bold"
          sx={{ w: "20px", h: "20px", color: "#fff" }}
        />
        <Body
          size="small"
          weight="bold"
          sx={{ color: "#fff", letterSpacing: "0.01em" }}
        >
          {label}
        </Body>
        <Box sx={{ flex: 1 }} /> {/* Spacer */}
        {isLoading ? (
          <ShimmerBox sx={{ width: "40px", height: "20px" }} />
        ) : (
          <Body
            size="small"
            weight="bold"
            sx={{ color: "#fff", letterSpacing: "0.01em" }}
          >
            {value}
          </Body>
        )}
      </Box>
    );
  },
);

function isItemSelected(pathname: string, itemPath?: string) {
  if (!itemPath) return false;
  // Use exact match for selection state
  return pathname.startsWith(itemPath);
}

// Add display name for better debugging
DesktopProfileCard.displayName = "DesktopProfileCard";

// Add display name for better debugging
ProfileRow.displayName = "ProfileRow";
